import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dropdown} from 'primereact/dropdown'
import {InputTextarea} from 'primereact/inputtextarea'
import {Tag} from 'primereact/tag'
import {Timeline} from 'primereact/timeline'
import {useCallback, useEffect, useRef} from 'react'
import {useInView} from 'react-intersection-observer'
import {ReactComponent as AuditLogIcon} from '../../../assets/icons/AuditLogIcon.svg'
import Avatar from '../../../assets/images/avatar.jpg'
import {ReactComponent as EmptyNoteList} from '../../../assets/images/note-list.svg'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {useAuth} from '../../authentication/AuthProvider'
import {noteKey, typeOptions} from '../../note/NoteConstant'
import {getPageNotes} from '../../note/NoteEndPoint'
import {useNoteCRUD, usePageNotes} from '../../note/query-keys/NoteKeysManager'
import {isArrayAndNotEmpty} from '../../observations/Utils'
import {usePatientContext} from '../information/StyleDetailInformationWrapper'

// TODO: Translate
const StyledNote = () => {
    useSignals()
    const {user} = useAuth()
    const {currentViewedUser} = usePatientContext()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {
        translateDatatable,
        translateKeys,
        translateCommons,
        translateOptions,
    } = TranslateUtils()

    //#region Note List
    const pageNumberRef = useRef(0)
    const pageable = {
        pageRows: 10,
        sortField: 'createdAt',
        sortOrder: -1,
    }
    const noteDataForm = useSignal({})
    const filterRequest = {}
    const {data: queryData} = usePageNotes(
        user?.id,
        0,
        pageable.pageRows,
        pageable.sortField,
        pageable.sortOrder,
        {
            ...filterRequest,
            userId: currentViewedUser?.id,
        },
        true,
    )
    //#endregion
    //#region Load more events
    const queryClient = useQueryClient()
    const observerRef = useRef()

    // Load more events when the page changes
    const loadMoreEvents = useCallback(async () => {
        if (pageNumberRef.current >= queryData?.totalPages) {
            return
        }

        const nextLogPageData = await getPageNotes(
            user?.id,
            pageNumberRef.current + 1,
            pageable.pageRows,
            pageable.sortField,
            pageable.sortOrder,
            {
                ...filterRequest,
                userId: currentViewedUser?.id,
            },
        )

        if (isArrayAndNotEmpty(nextLogPageData?.content)) {
            const {noteKeys} = QueryKeyManager(QueryKeyTypes.note, user?.id)

            queryClient.setQueryData(
                noteKeys.page({
                    pageNumber: 0,
                    pageRows: pageable.pageRows,
                    sortField: pageable.sortField,
                    sortOrder: pageable.sortOrder,
                    filterRequest: {
                        ...filterRequest,
                        userId: currentViewedUser?.id,
                    },
                }),
                oldData => {
                    if (!nextLogPageData?.last) {
                        return {
                            ...oldData,
                            content: [
                                ...oldData.content,
                                ...nextLogPageData.content,
                            ],
                        }
                    } else {
                        return {
                            ...oldData,
                            content: [
                                ...oldData.content,
                                ...nextLogPageData.content,
                                {
                                    isEnd: true,
                                    preview: 'End',
                                    color: 'black',
                                    size: 12,
                                },
                            ],
                        }
                    }
                },
            )

            pageNumberRef.current++
        }

        isLoadingMore.current = false
    }, [queryData?.totalPages, pageNumberRef.current])

    const activeId = useSignal(null)

    const {
        ref: inViewRef,
        inView,
        entry,
    } = useInView({
        /* Optional options */
        threshold: 0,
    })

    const isLoadingMore = useRef(false)
    useEffect(() => {
        if (!!inView && !isLoadingMore.current) {
            isLoadingMore.current = true
            loadMoreEvents()
        }
    }, [inView])

    //#endregion

    //#region Render
    const renderNewestNote = noteId => {
        let newestNote = {}
        let fullName = ''

        if (queryData?.content && queryData?.content?.length) {
            if (noteId) {
                newestNote = queryData.content?.find(
                    item => item?.id === noteId,
                )
            } else {
                newestNote = queryData.content[0]
            }
        }

        if (newestNote?.modifiedBy) {
            if (newestNote?.modifiedBy?.firstName)
                fullName += newestNote?.modifiedBy?.firstName
            if (newestNote?.modifiedBy?.lastName)
                fullName += ' ' + newestNote?.modifiedBy.lastName
        }

        return (
            <div
                className="col-12 glassmorphism no-focus"
                style={{height: '30rem'}}>
                <div
                    className="bg-yellow-50 border-round p-2 font-semibold overflow-y-auto"
                    style={{height: '25rem'}}>
                    {/* Note Content */}
                    <div className="flex flex-column">
                        {newestNote?.type && (
                            <div className="mb-2">
                                <Tag
                                    severity={'warning'}
                                    value={`${newestNote?.type}`}
                                />
                            </div>
                        )}
                        <div
                            className="mb-2 px-2 font-bold"
                            style={{
                                overflowWrap: 'break-word',
                                whiteSpace: 'pre-line',
                            }}>
                            {newestNote?.preview}
                        </div>
                    </div>
                </div>
                {/* Author */}
                <div className="flex justify-content-between align-items-center">
                    <span className="font-normal text-sm">
                        {newestNote?.createdAt}
                    </span>
                    {newestNote?.modifiedBy && (
                        <div className="flex align-items-center">
                            <span className="mr-2 font-normal">Noted by</span>
                            <h5 className="font-bold mr-3 my-0">{fullName}</h5>
                            <img
                                alt={'avatar'}
                                src={
                                    newestNote?.modifiedBy?.avatar
                                        ? newestNote?.modifiedBy?.avatar
                                        : Avatar
                                }
                                className={`bg-white border-circle p-1`}
                                style={{width: '50px', height: '50px'}}
                            />
                        </div>
                    )}
                </div>
            </div>
        )
    }
    //#endregion

    //#region Submit
    const {mutateAsync: createOrUpdateNote} = useNoteCRUD(
        QueryCRUDTypes.create,
        user?.id,
    )
    const handleOnSubmit = async () => {
        let toPostData = {
            ...noteDataForm.value,
            userId: currentViewedUser?.id,
        }

        const noteData = await createOrUpdateNote(toPostData)

        if (noteData?.id) {
            const {noteKeys} = QueryKeyManager(QueryKeyTypes.note, user?.id)

            //TODO: Temporary Refetch Table
            queryClient.invalidateQueries({
                queryKey: noteKeys.pattern(),
            })

            // try {
            //     reloadDataTable(QueryTableKey + id, noteData)
            // } catch (err) {
            //     console.log('Fail to reload table')
            // }

            noteDataForm.value = {}
        }
    }
    //#endregion
    const handleOnChange = (key, value) => {
        if (noteDataForm.value) {
            noteDataForm.value = {
                ...noteDataForm.value,
                [key]: value,
            }
        } else {
            noteDataForm.value = {
                [key]: value,
            }
        }
    }
    const renderNoteForm = () => {
        return (
            <>
                <div
                    key={`flex`}
                    className="flex flex-column col-12"
                    style={{borderBottom: '1px dashed lightgray'}}>
                    <div className="col-12" style={{position: 'relative'}}>
                        <div className="sticky">
                            <div>
                                <p className="m-0 p-0 px-2 font-normal text-lg">
                                    <span className="font-bold text-darkcyan ">
                                        Notes
                                    </span>{' '}
                                    are the process of recording health
                                    information, symptoms, and treatment plans.
                                    They help doctors monitor progress and
                                    ensure appropriate care.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className={'col-12'}>
                        <div className="flex justify-content-between align-items-start">
                            <h5
                                className={
                                    'font-bold p-0 text-sm sm:text-sm md:text-xl lg:text-xl'
                                }>
                                {translateKeys('Type')}
                            </h5>
                            <Tag
                                value={translateCommons(
                                    'This field is required !',
                                )}
                                severity="danger"
                                icon="pi pi-info-circle"></Tag>
                        </div>
                        <div className="grid col-12">
                            <Dropdown
                                className={`p-0 m-0 prevent-select w-full`}
                                options={translateOptions(typeOptions)}
                                onChange={e =>
                                    handleOnChange(noteKey.type, e.value)
                                }
                                placeholder={translateCommons(
                                    'Select your note type',
                                )}
                                optionLabel="label"
                                optionValue="value"
                                value={noteDataForm.value[noteKey.type]}
                                filter
                                filterBy="label"
                                filterPlaceholder={translateCommons(
                                    'Search here',
                                )}
                                emptyMessage={translateCommons(
                                    'No records found',
                                )}
                                showFilterClear
                                resetFilterOnHide
                                showClear
                                emptyFilterMessage={translateCommons(
                                    'No results found',
                                )}
                                style={{
                                    '--inputColor': 'rgba(68, 184, 166, 0.1)',
                                }}
                            />
                        </div>
                    </div>
                    <div className={'col-12'}>
                        <div className="flex justify-content-between align-items-start">
                            <h5
                                className={
                                    'font-bold p-0 text-sm sm:text-sm md:text-xl lg:text-xl'
                                }>
                                {translateKeys('Preview')}
                            </h5>
                            <Tag
                                value={translateCommons(
                                    'This field is required !',
                                )}
                                severity="danger"
                                icon="pi pi-info-circle"></Tag>
                        </div>
                        <div className="grid col-12">
                            <InputTextarea
                                className={`prevent-select w-full`}
                                placeholder={translateKeys(
                                    'Input your preview here... (Field is requried)',
                                )}
                                rows={5}
                                cols={30}
                                autoResize
                                value={
                                    noteDataForm.value[noteKey.preview] || ''
                                }
                                onChange={e =>
                                    handleOnChange(
                                        noteKey.preview,
                                        e.target.value,
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>
                <div className="flex col-12 py-3">
                    <Button
                        className={`col-12 p-button-lg font-bold mr-2`}
                        icon={'pi pi-send'}
                        label={'SUBMIT'}
                        onClick={handleOnSubmit}
                    />
                </div>
            </>
        )
    }
    return (
        <div className="w-full ">
            <div className="w-full">
                {/* Header */}
                {/* <div className="flex align-items-center">
                    <div className="flex align-items-center">
                        <i className="pi pi-bookmark-fill mr-2"></i>
                        <h4 className="m-0 mr-2">Total </h4>
                        <h4 className="font-bold m-0 mr-3">
                            {queryData?.totalElements || 0}
                        </h4>
                        <h4 className="m-0 mr-3">Notes </h4>
                    </div>
                </div> */}
                {queryData?.content && queryData?.content?.length ? (
                    <div className="grid align-items-stretch">
                        <div className="col-12 lg:col-8 md:col-8">
                            {/* Newest Note */}
                            <div className="flex align-items-center mb-3">
                                <div>
                                    <i className="pi pi-comments icon-size mr-2" />
                                </div>
                                <div>
                                    <h5 className="font-bold m-0">Newest</h5>
                                </div>
                            </div>
                            {renderNewestNote(activeId.value)}
                        </div>
                        <div className="col-12 lg:col-4 md:col-4 h-full">
                            <div className="flex align-items-center mb-3">
                                <div>
                                    <AuditLogIcon className="icon-size mr-2" />
                                </div>
                                <div>
                                    <h5 className="mb-0 font-bold">
                                        {translateDatatable('Note List')}
                                    </h5>
                                </div>
                            </div>

                            <div
                                className="overflow-y-auto w-full"
                                style={{maxHeight: '30rem'}}>
                                {!queryData?.content?.length ? (
                                    <div className="w-full text-center">
                                        <EmptyNoteList
                                            className="icon-size p-6 w-full"
                                            height={200}
                                        />
                                        <span className="font-bold">
                                            There are currently no notes.
                                        </span>
                                    </div>
                                ) : (
                                    <Timeline
                                        value={queryData?.content || []}
                                        marker={item => {
                                            let isActive = false
                                            if (!activeId.value) {
                                                isActive =
                                                    queryData.content[0]?.id ===
                                                    item?.id
                                            } else {
                                                isActive =
                                                    activeId.value === item?.id
                                            }

                                            return (
                                                <span
                                                    className={`flex align-items-center justify-content-center border-circle z-1 shadow-1`}
                                                    style={{
                                                        backgroundColor:
                                                            isActive ||
                                                            item?.isEnd
                                                                ? 'darkcyan'
                                                                : '',
                                                        color:
                                                            isActive ||
                                                            item?.isEnd
                                                                ? 'white'
                                                                : 'black',
                                                        height: '2rem',
                                                        width: '2rem',
                                                        marginLeft: `8px`,
                                                    }}>
                                                    <i
                                                        className={
                                                            !item?.isEnd
                                                                ? isActive
                                                                    ? 'pi pi-check-circle'
                                                                    : 'pi pi-align-left'
                                                                : 'pi pi-check'
                                                        }></i>
                                                </span>
                                            )
                                        }}
                                        content={(item, index) => (
                                            <div
                                                className="flex justify-content-between align-items-center w-full"
                                                ref={
                                                    index ===
                                                    queryData?.content?.length -
                                                        1
                                                        ? inViewRef
                                                        : null
                                                }>
                                                <div
                                                    className={`${
                                                        !item?.isEnd
                                                            ? 'cursor-pointer hover:bg-teal-50'
                                                            : ''
                                                    }  border-round p-2 w-full`}
                                                    onClick={() =>
                                                        (activeId.value =
                                                            item?.id)
                                                    }>
                                                    {!item?.isEnd ? (
                                                        <div>
                                                            <small className="text-color-secondary">
                                                                {
                                                                    item?.createdAt
                                                                }
                                                            </small>
                                                            <div>
                                                                Notes about{' '}
                                                                <span className="font-bold text-darkcyan">
                                                                    {item?.type}
                                                                </span>{' '}
                                                                {item?.modifiedBy && (
                                                                    <>
                                                                        written
                                                                        by{' '}
                                                                        <span className="font-bold text-darkcyan">
                                                                            {item
                                                                                ?.modifiedBy
                                                                                ?.firstName
                                                                                ? item
                                                                                      ?.modifiedBy
                                                                                      ?.firstName
                                                                                : ''}
                                                                            {item
                                                                                ?.modifiedBy
                                                                                ?.lastName
                                                                                ? ' ' +
                                                                                  item
                                                                                      ?.modifiedBy
                                                                                      ?.lastName
                                                                                : ''}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="font-bold">
                                                            {item?.preview}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    />
                                )}
                            </div>
                            <hr />
                            <div className="flex align-items-center justify-content-end mt-2 text-600">
                                <div className="flex-grow-1 font-semibold">
                                    <i className="pi pi-copy mr-2"></i>
                                    Total Note
                                </div>
                                <span className="font-bold text-darkcyan mr-2">
                                    {queryData?.totalElements || 0}
                                </span>{' '}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="w-full text-center">
                        <EmptyNoteList
                            className="icon-size p-6 w-full"
                            height={200}
                        />
                        <span className="font-bold">
                            There are currently no notes.
                        </span>
                    </div>
                )}
            </div>

            <hr />
            <div className="w-full  m-0 p-0">
                <div className="flex align-items-center">
                    <div>
                        <i className="pi pi-plus-circle icon-size mr-2" />
                    </div>
                    <div>
                        <h5 className="font-bold m-0">Create new</h5>
                    </div>
                </div>{' '}
            </div>
            <div className="w-full m-0 p-0">{renderNoteForm()}</div>
        </div>
    )
}

export default StyledNote
